// @flow
import React from 'react';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { numberWithCommas, statusTrans } from 'lib/common';
import { camelCase } from 'lodash';
import 'moment/locale/ko';
import './OrderInvoiceTemplate.scss';

type Props = {
  order: any,
  user: any,
  logged: boolean,
};

const OrderInvoiceTemplate = (props: Props) => (
  <div className="OrderInvoiceTemplate">
    <Helmet>
      <title>{`캐시바이-거래명세서-${moment().format('YYYY-MM-DD-HH:mm:ss')}`}</title>
    </Helmet>
    <div className="main">
      <div className="offer order-offer">
        <div className="offer-process">
          <div className="cart-items">
            <div data-top>
              <h2 data-title>거래명세서</h2>
              <img
                src="https://images.cacheby.com/datasee_stamp.png"
                alt="stamp"
                className="stamp"
              />
              <dl data-top-info>
                <dt data-label>주문번호</dt>
                <dd data-value>{props.order.id}</dd>
                <dt data-label>날짜</dt>
                <dd data-value>{moment(props.order.created_at).format('YYYY-MM-DD')}</dd>
                <dt data-label>상태</dt>
                <dd data-value>
                  {props.order.status === 'cancelled'
                    ? statusTrans(
                        camelCase([
                          'status',
                          props.order.status,
                          'by',
                          props.order.cancellation.by,
                        ]),
                      )
                    : statusTrans(camelCase(['status', props.order.status]))}
                </dd>
              </dl>
            </div>

            <div data-buyer>
              <h2 data-title>구매자</h2>
              <table data-table>
                <tbody>
                  <tr>
                    <th data-label scope="row">
                      회사이름(소속)
                    </th>
                    <td data-value>{props.logged ? props.user.displayName : ''}</td>
                    <th data-label scope="row">
                      담당자(이름)
                    </th>
                    <td data-value>{props.order.address.billing.name.full}</td>
                  </tr>
                  <tr>
                    <th data-label scope="row">
                      연락처(휴대폰)
                    </th>
                    <td data-value>{props.order.address.billing.mobile}</td>
                    <th data-label scope="row">
                      이메일
                    </th>
                    <td data-value>{props.order.customer.email}</td>
                  </tr>
                  <tr>
                    <th data-label scope="row">
                      주소
                    </th>
                    <td data-value>
                      {`${props.order.address.billing.address1} ${props.order.address.billing.address2}`}
                    </td>
                    <th data-label scope="row">
                      사업자번호
                    </th>
                    <td data-value>{props.order.address.billing.phone}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div data-seller>
              <h2 data-title>판매자</h2>
              <table data-table>
                <tbody>
                  <tr>
                    <th data-label scope="row">
                      상호(법인명/서비스명)
                    </th>
                    <td data-value>(주)데이터씨 / 캐시바이</td>
                    <th data-label scope="row">
                      대표
                    </th>
                    <td data-value>최영진</td>
                  </tr>
                  <tr>
                    <th data-label scope="row">
                      업태
                    </th>
                    <td data-value>도매 및 소매업</td>
                    <th data-label scope="row">
                      업종
                    </th>
                    <td data-value>상품종합 중개업</td>
                  </tr>
                  <tr>
                    <th data-label scope="row">
                      연락처(휴대폰)
                    </th>
                    <td data-value>02-585-1342</td>
                    <th data-label scope="row">
                      이메일
                    </th>
                    <td data-value>info@cacheby.com</td>
                  </tr>
                  <tr>
                    <th data-label scope="row">
                      주소
                    </th>
                    <td data-value>서울시 서초구 동광로1길 52, 1층</td>
                    <th data-label scope="row">
                      사업자번호
                    </th>
                    <td data-value>464-81-00805</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <table className="table">
              <thead>
                <tr>
                  <th>번호</th>
                  <th>카탈로그 번호</th>
                  <th>물품명</th>
                  <th>단위</th>
                  <th>수량</th>
                  <th>단가</th>
                  <th>가격</th>
                  <th>세액</th>
                </tr>
              </thead>

              <tbody>
                {props.order.items.map((item, index) => (
                  <tr key={item.id} className="cart-item" data-item={item.id}>
                    <td className="cart-item-number">
                      <span className="cart-item-number-label hidden">번호</span>
                      <span className="cart-item-number-value">{index + 1}</span>
                    </td>
                    <td className="cart-item-number">
                      <span className="cart-item-number-label hidden">카탈로그 번호</span>
                      <span className="cart-item-number-value">
                        {item.product_price.part_number}
                      </span>
                    </td>
                    <td className="cart-item-name">
                      <div className="cart-item-summary">
                        <span className="cart-item-name">{item.product_price.description}</span>
                      </div>
                    </td>

                    <td className="cart-item-number">
                      <span className="cart-item-number-label hidden">단위</span>
                      <span className="cart-item-number-value">{item.product_price.unit}</span>
                    </td>
                    <td className="cart-item-quantity">
                      <span className="cart-item-quantity-label hidden">수량</span>
                      <span className="cart-item-quantity-value">{item.quantity}</span>
                    </td>

                    <td className="cart-item-price">
                      {item.price.sale.raw !== item.price.original.raw && (
                        <span className="cart-item-compare-price">
                          {numberWithCommas(item.price.original.raw)}원
                        </span>
                      )}
                      <span className="cart-item-sale-price">
                        {numberWithCommas(item.price.sale.raw)}원
                      </span>
                    </td>
                    <td className="cart-item-price">
                      {item.total.price.sale.raw !== item.total.price.original.raw && (
                        <span className="cart-item-compare-price">
                          {numberWithCommas(item.total.price.original.raw)}원
                        </span>
                      )}
                      <span className="cart-item-sale-price">
                        {numberWithCommas(item.total.price.sale.raw)}원
                      </span>
                    </td>
                    <td className="cart-item-price">
                      <span className="cart-item-sale-price">
                        {numberWithCommas(item.total.taxed.raw)}원
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div data-bottom>
              <div data-notes>
                <div data-title>추가설명</div>
                <span data-desc>
                  연구소 재고 및 구매관리 프로그램 <a href="https://labsby.com">랩스바이 LabsBy</a>,
                  지금 무료로 사용 해보세요.
                </span>
                <br />
                <span data-desc>* 10만원 이하 주문시 배송비가 추가 됩니다.</span>
                <br />
                <span data-desc>
                  * 배송일이 2주 이상 걸리는 품목은 수입 발주 품목으로 반품 불가합니다.
                </span>
                <br />
                <span data-desc>* 예금주 : 데이터씨 기업은행 033-502993-01-019</span>
              </div>
              <dl data-toatal-area>
                <dt data-label="sumPrice">총합</dt>
                <dd className="sumPrice" data-value="sumPrice">
                  {props.order.total.price.sale.raw !== props.order.total.price.original.raw && (
                    <span className="cart-items-compare-price">
                      {numberWithCommas(props.order.total.price.original.raw)}원
                    </span>
                  )}
                  <span className="cart-items-sale-price">
                    {numberWithCommas(props.order.total.price.sale.raw)}원
                  </span>
                  {props.order.total.price.sale.raw !== props.order.total.price.original.raw && (
                    <span className="cart-items-discounted">
                      (-{numberWithCommas(props.order.total.discounted.raw)}원)
                    </span>
                  )}
                </dd>
                <dt data-label="sumVat">배송비</dt>
                <dd data-value="sumVat">
                  {numberWithCommas(props.order.total.shipping.fee.sale.raw)}원
                </dd>
                <dt data-label="sumTotal">부가세</dt>
                <dd data-value="sumTotal">{numberWithCommas(props.order.total.taxed.raw)}원</dd>
                <br />
                <dt data-label="sumTotal">총계</dt>
                <dd data-value="sumTotal">{numberWithCommas(props.order.total.amount.raw)}원</dd>
              </dl>
            </div>
            <div className="logo-wrapper">
              <img src={'https://images.cacheby.com/logo.svg'} alt="logo" className="logo" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default OrderInvoiceTemplate;



// WEBPACK FOOTER //
// ./src/components/order/OrderInvoiceTemplate/OrderInvoiceTemplate.js